import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdentityOrganisationApiClient } from 'src/app/services/api-clients/identity-organisation.api-client';
import { ToastService } from 'src/app/services/toast.service';
import { Subscription } from 'rxjs';
import { User } from 'src/app/models/user';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { OnlyLNumbersValidator } from 'src/app/directives/only-numbers-validator.directive';
import { ClipboardService } from 'src/app/services/clipboard.service';
import { SettingsService } from '../../settings.service';
import { SettingsBreadCrumbService, BreadCrumbOptions } from '../../settings-breadcrumb.service';
import { IdsForSettingsService } from '../../ids-for-settings.service';

@Component({
  templateUrl: './settings-organisation-overview.page.html'
})
export class SettingsOrganisationOverviewPage implements OnInit, OnDestroy {

  selectedOrganisationId: string = "";
  organisation!: any;
  user!: User;
  progressSubscription!: number;
  form: UntypedFormGroup;
  kvkNumberControl!: FormControl;
  signatureAmount: number = -1;
  remainingSignatures: number = -1;
  signatureProgress: number = 0;
  userCount: number = -1;
  translations: any = <any>{};
  subdate: Date = new Date(Date.now());
  whitelabelProviders: any[] = [];

  organisationIdSubscription: Subscription;

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private organisationApiClient: IdentityOrganisationApiClient,
    private formBuilder: UntypedFormBuilder,
    private clipboardService: ClipboardService,
    private idsForSettingsService: IdsForSettingsService,
    private settingsService: SettingsService,
    private settingsBreadCrumbService: SettingsBreadCrumbService) {

    // setup
    this.kvkNumberControl = new UntypedFormControl('', [OnlyLNumbersValidator(), Validators.minLength(8), Validators.maxLength(8)]);
    this.form = this.formBuilder.group({
      kvk: this.kvkNumberControl
    });
    // get data
    this.user = this.settingsService.user;
    this.organisationIdSubscription = this.idsForSettingsService.selectedOrganisationId$.subscribe(organisationId => {
      if (organisationId === this.selectedOrganisationId) return;
      this.selectedOrganisationId = organisationId;
      // setup specialised breadcrumb
      this.settingsBreadCrumbService.SetOrganisationCrumbs(BreadCrumbOptions.Organisation_Overview(this.selectedOrganisationId));
      this.organisationApiClient.getOrganisation(organisationId).subscribe(organisationData => {
        this.organisation = organisationData;
        this.remainingSignatures = 0;
        this.signatureAmount = 0;
        this.organisation.signingBundles.forEach((item: any) => {
          this.remainingSignatures += item.remainingSignatures;
          this.signatureAmount += item.signatureAmount;
        });
        this.signatureProgress = (this.remainingSignatures / (this.signatureAmount == 0 ? 1 : this.signatureAmount)) * 100;
        this.userCount = this.organisation.users.length;
        this.subdate = new Date(this.organisation.organisation.subscriptionStartDate);
        let renewalDate = new Date(this.subdate.setFullYear(new Date(Date.now()).getFullYear()));
        this.subdate = renewalDate;
        if (renewalDate < new Date(Date.now())) {
          this.subdate = new Date(renewalDate.setFullYear((new Date(Date.now())).getFullYear() + 1));
        }
        let subscriptionRemainingDays = (this.subdate.getTime() - new Date(Date.now()).getTime()) / (1000 * 3600 * 24);
        this.progressSubscription = (subscriptionRemainingDays / 365) * 100;
      });
    });
  }
  ngOnDestroy(): void {
    this.organisationIdSubscription.unsubscribe();
  }

  ngOnInit(): void {
    this.getTranslations();
    this.translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.Error',
      'Settings.OrganisationSection.KVKerror'
    ]).subscribe(translation => {
      this.translations.error = translation['Toasts.Error'];
      this.translations.KVKerror = translation['Settings.OrganisationSection.KVKerror'];
    });
  }

  updateAttribute(field: string, event: any) {
    if (!this.form.controls[field].invalid) {
      (event.target as HTMLInputElement).classList.remove('is-invalid');
    }
    if (field == "kvk") this.organisation.kvk = event.target.value;
    else this.organisation.organisation[field] = event.target.value;
  }

  async updateKvk() {
    if (this.form.status === "INVALID") {
      document.getElementById('kvk')!.classList.add('is-invalid');
      this.toastService.showError(this.translations.error, this.translations.KVKerror);
      return;
    }
    document.getElementById('kvk')!.classList.remove('is-invalid');
    this.settingsService.updateOrganisation(this.organisation);
  }

  copyOrganisationIdToClipboard() {
    this.clipboardService.Copy(this.selectedOrganisationId);
  }
}
